import { useCallback, useEffect, useRef } from "react";

type Props = {
  follow: boolean;
  onCancel: () => void;
  scrollContainer: HTMLElement | Document | null;
  contentElement: HTMLElement | null;
};

/**
 * useScrollFollower automatically scrolls a container to the bottom when the inner content changes size.
 *
 * If a manual scroll is detected, the automatic scrolling is canceled.
 */
export const useScrollFollow = ({ follow, onCancel, contentElement, scrollContainer }: Props) => {
  // Flag to indicate if automatic scrolling is in progress so that we know when to cancel manual scrolling
  const isAutoScrolling = useRef(false);

  const scrollToEnd = useCallback(() => {
    isAutoScrolling.current = true;

    if (Features.BuildSidebar) {
      // New UI works better by scrolling to the end of the scroll container. Ignoring things like scroll margin offsets etc.
      if (scrollContainer) {
        (scrollContainer as HTMLElement).scrollTop = (scrollContainer as HTMLElement).scrollHeight;
      }
    } else {
      // Classic UI uses scrollIntoView since we're scrolling the entire window.
      contentElement?.scrollIntoView({ block: "end" });
    }

    // Add a delay before resetting the flag to allow for manual scrolling
    setTimeout(() => {
      isAutoScrolling.current = false;
    }, 100);
  }, [scrollContainer, contentElement]);

  // If a manual scroll is detected, cancel automatic scrolling
  const cancelScroll = useCallback(() => {
    if (!isAutoScrolling.current) {
      onCancel();
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      scrollToEnd();
    });

    if (follow) {
      if (contentElement && scrollContainer) {
        scrollToEnd();
        scrollContainer?.addEventListener("scroll", cancelScroll);
        resizeObserver.observe(contentElement);
      }
    }

    return () => {
      scrollContainer?.removeEventListener("scroll", cancelScroll);
      resizeObserver.disconnect();
    };
  }, [follow, scrollContainer, contentElement, scrollToEnd, cancelScroll]);
};
